// EditTripModal — slim container/orchestrator.
//
// Behavior-preserving refactor (issue #240, spec task 10.7). This file keeps the
// SAME module path, the SAME default export `EditTripModal`, and the SAME public
// props `{ trip, onClose, onSave }` as the original 772-LOC component. All form
// state + meeting/event mutations now live in `useTripForm`, the save flow lives
// in `useSaveTrip`, and the rendered UI is composed from the memoized
// presentation components — preserving the exact element hierarchy, attributes,
// and visible text the characterization snapshots pinned.
//
// Render contract (matching the source DOM exactly):
//   - When a sub-view flag is active the modal renders one of the full-screen
//     child forms INSTEAD of the main editor body (the source's `getSubView`
//     short-circuit). The container computes the same priority condition and,
//     when active, returns `SubViewRouter` directly (which resolves to the
//     correct child form in add-meeting → edit-meeting → pdf-updater → add-event
//     order).
//   - Otherwise the main modal renders inside `ModalShell` (overlay + chrome +
//     header with the trip-name input), containing — IN SOURCE ORDER —
//     `TripInfoHeader`, `AddEventSection`, `MeetingsSection`, `ModalFooter`.
//
// Validates: Requirements 1.1, 1.2, 2.1, 2.2, 2.3

import useTripForm from './editTripModal/hooks/useTripForm';
import useSaveTrip from './editTripModal/hooks/useSaveTrip';
import ModalShell from './editTripModal/presentation/ModalShell';
import TripInfoHeader from './editTripModal/presentation/TripInfoHeader';
import AddEventSection from './editTripModal/presentation/AddEventSection';
import MeetingsSection from './editTripModal/presentation/MeetingsSection';
import ModalFooter from './editTripModal/presentation/ModalFooter';
import SubViewRouter from './editTripModal/presentation/SubViewRouter';

const EditTripModal = ({ trip, onClose, onSave }) => {
  const form = useTripForm({ trip });
  const { isSaving, handleSave } = useSaveTrip({
    editedTrip: form.editedTrip,
    onSave,
    onClose,
  });

  // ── Sub-view short-circuit (source's `getSubView` + `if (subView) return`) ──
  // When any sub-view flag is active, the modal renders a full-screen child form
  // INSTEAD of the main editor body. SubViewRouter resolves the active form in
  // the source priority order (add-meeting → edit-meeting → pdf → add-event).
  if (form.showAddMeeting || form.editingMeeting || form.showPDFUpdater || form.showAddEvent) {
    return (
      <SubViewRouter
        showAddMeeting={form.showAddMeeting}
        editingMeeting={form.editingMeeting}
        showPDFUpdater={form.showPDFUpdater}
        showAddEvent={form.showAddEvent}
        newEventType={form.newEventType}
        editedTrip={form.editedTrip}
        onAddMeeting={form.handleAddMeeting}
        onEditMeeting={form.handleEditMeeting}
        onPDFUpload={form.handlePDFUpload}
        onAddManualEvent={form.handleAddManualEvent}
        onCancelAddMeeting={() => form.setShowAddMeeting(false)}
        onCancelEditMeeting={() => form.setEditingMeeting(null)}
        onClosePDFUpdater={() => form.setShowPDFUpdater(false)}
        onCancelAddEvent={() => {
          form.setShowAddEvent(false);
          form.setNewEventType('');
        }}
      />
    );
  }

  // ── Main modal, rendered in the exact source order ──────────────────────────
  return (
    <ModalShell
      tripName={form.editedTrip.tripName}
      onTripNameChange={(value) =>
        form.setEditedTrip(prev => ({ ...prev, tripName: value }))
      }
      onClose={onClose}
    >
      <TripInfoHeader dateRange={form.tripDateRange} counts={form.tripCounts} />
      <AddEventSection
        onUploadPDF={() => form.setShowPDFUpdater(true)}
        onAddEvent={form.handleManualAddEvent}
        onAddMeeting={() => form.setShowAddMeeting(true)}
      />
      <MeetingsSection
        summary={form.meetingsSummary}
        meetings={form.editedTrip.meetings}
        onClearAll={form.clearAllMeetings}
        onEditMeeting={form.setEditingMeeting}
        onDeleteMeeting={form.handleDeleteMeeting}
      />
      <ModalFooter isSaving={isSaving} onClose={onClose} onSave={handleSave} />
    </ModalShell>
  );
};

export default EditTripModal;
